import React, { cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import { debounce, isEmpty } from 'lodash';

import { faCheckCircle, faPlus, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { faBuildoutLogo } from 'helpers/custom_fontawesome_icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Form, ListGroup, Overlay, OverlayTrigger, Popover, Tooltip } from 'react-bootstrap-5';

import { Button } from 'components/ui/button';
import CreateListModal from './create_list_modal';
import pluralize from 'pluralize';
import { offMarketPropertySearch } from 'bundles/connect/helpers/prospect_api';
import { addPropertiesToCallList, fetchCallLists } from '../../helpers/prospect_api';
import { type CallList, type Criteria } from 'bundles/connect/types';

type AddToListProps = {
  children: React.ReactElement;
  criteria?: Criteria;
  itemCount: number;
  itemIds: Set<string>;
  placement?: string;
  popoverTargetRef?: React.RefObject<HTMLElement>;
  selectAll?: boolean;
  tooltip?: boolean;
}

export default function AddToList({
  criteria,
  children,
  itemCount,
  selectAll,
  itemIds,
  popoverTargetRef,
  tooltip = true,
}: AddToListProps) {
  const [showOverlay, setShowOverlay] = useState<boolean>();
  const [showCreateModal, setShowCreateModal] = useState<boolean>();
  const [callLists, setCallLists] = useState<CallList[]>([]);
  const [savedTo, setSavedTo] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const triggerRef = useRef(null);
  const overlayId = Math.floor(Math.random() * 100000);

  const debouncedSearch = useCallback(debounce((search) => {
    fetchCallLists({ 'q[name_or_description_cont]': search }).then(setCallLists);
  }, 300), []);

  useEffect(() => {
    if (showOverlay) {
      setSavedTo(undefined);
      setSaving(false);
      setSearchText('');
    }
  }, [showOverlay]);

  useEffect(() => {
    if (savedTo) {
      setTimeout(() => handleCloseOverlay(), 3000);
    }
  }, [savedTo]);

  useEffect(() => {
    if (showOverlay) debouncedSearch(searchText);
  }, [showOverlay, searchText]);

  const handleCloseOverlay = () => {
    setShowOverlay(false);
  };

  const handleEnterPressed = useCallback((e) => {
    e.preventDefault();
    if (searchText.length > 0 && callLists.length === 0) handleShowCreateModal(e);
  }, [searchText, callLists]);

  const assembleCallListItems = async () => {
    let ids = [...itemIds];

    if (selectAll && criteria) {
      const results = await offMarketPropertySearch(criteria, 10_000);
      ids = (results.hits || []).map(hit => hit.propertyId);
    }

    return ids;
  };

  const handleAddToList = (callList) => {
    setShowCreateModal(false);
    setShowOverlay(true);
    setSaving(true);

    (async () => {
      const callListItems = await assembleCallListItems();

      if (window.gtag) window.gtag('event', 'connect_off_market_property_added_to_call_list');

      addPropertiesToCallList(callList.id, callListItems)
        .then(() => setSavedTo(callList.name))
        .finally(() => setSaving(false));
    })();
  };

  const handleShowCreateModal = (e) => {
    e.stopPropagation();

    handleCloseOverlay();
    setShowCreateModal(true);
  };

  const popoverTitle = (
    <h6 className="px-3 py-2 m-0 border-bottom">
      Add {pluralize('Item', Math.min(itemCount, 10000), true)} To List
    </h6>
  );

  const popover = (
    <Popover id={`add-to-list-popover-${overlayId}`} style={{ maxWidth: '500px', minWidth: '320px' }}>
      {saving && (
        <Popover.Body>
          {popoverTitle}
          <div className="text-center animate__animated animate__fadeIn pt-3" style={{ fontSize: '1rem' }}>
            <FontAwesomeIcon beatFade className="me-2" icon={faBuildoutLogo} />
            Saving...
          </div>
        </Popover.Body>
      )}
      {!saving && savedTo && (
        <Popover.Body>
          {popoverTitle}
          <div className="text-center animate__animated animate__fadeIn pt-3" style={{ fontSize: '1rem' }}>
            <FontAwesomeIcon className="me-2" icon={faCheckCircle} />
            Saved to "{savedTo}"
          </div>
        </Popover.Body>
      )}
      {!saving && !savedTo && (
        <>
          <Popover.Body className="px-0 pt-2 pb-0">
            {popoverTitle}
            <Form className="pt-3" onSubmit={handleEnterPressed}>
              <Form.Group className="mb-2 px-3">
                <div className="with-icon">
                  <div className="icon search-icon">
                    <FontAwesomeIcon icon={faSearch} />
                  </div>

                  <Form.Control
                    autoFocus
                    placeholder="Search by list name"
                    type="text"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                  />
                </div>
              </Form.Group>
            </Form>

            {isEmpty(callLists) ? (
              <div className="py-2 px-3">No results found</div>
            ) : (
              <ListGroup
                className="mx-3 my-2 border rounded"
                style={{ height: 200, overflow: 'auto' }}
                variant="flush"
              >
                {
                  callLists.map(callList => (
                    <ListGroup.Item
                      action
                      className="border-bottom-1"
                      key={callList.id}
                      onClick={() => handleAddToList(callList)}
                    >
                      <div className="w-100 text-truncate">
                        {callList.name}
                      </div>
                    </ListGroup.Item>
                  ))
                }
              </ListGroup>
            )}
          </Popover.Body>
          <div className="border-top d-flex align-items-center justify-content-between p-2">
            <a className="py-2 px-3 d-block" href="/prospect/connect_call_lists">Manage Lists</a>
            <Button onClick={handleShowCreateModal}>
              <FontAwesomeIcon className="me-2" icon={faPlus} size="sm" />
              Create New List
            </Button>
          </div>
        </>
      )}
    </Popover>
  );

  const renderTooltip = (
    <Tooltip id={`add-to-list-tooltip-${overlayId}`}>
      Add to List
    </Tooltip>
  );

  // If there is a provided popoverTargetRef, we don't need a ref on the children.
  // If there is no provided popoverTargetRef, put a ref on the children and use that for the popover target
  const modifiedChildren = cloneElement(children, {
    onClick: () => setShowOverlay(true),
    ref: popoverTargetRef?.current ? undefined : triggerRef
  });

  return (
    <>
      <Overlay
        flip={true}
        placement="auto"
        rootClose
        show={showOverlay}
        target={popoverTargetRef?.current || triggerRef.current}
        onHide={() => setShowOverlay(false)}
      >
        {popover}
      </Overlay>

      {tooltip ? (
        <OverlayTrigger
          overlay={renderTooltip}
          placement="top"
          popperConfig={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 8],
                },
              }
            ]
          }}
        >
          {modifiedChildren}
        </OverlayTrigger>
      ) : modifiedChildren}

      <CreateListModal
        defaultName={searchText}
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleAddToList}
      />
    </>
  );
}
